import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { DEAL_TYPES, type DealType } from '../lib/commission/stages';
import { createDeal } from '../lib/deals';
import { splitPersonName } from '../lib/import/normalize';
import { supabase, unwrap } from '../lib/supabase';
import { ContactPicker } from './Pickers';
import { useToast } from './Toast';
import { Button, ErrorBox, Field, FieldGroup, Input, Pill, Select } from './ui';

/** Creates a contact with just a name, so a deal can be logged without leaving the form. */
async function createNameOnlyContact(typed: string): Promise<{ id: string; name: string }> {
  const p = splitPersonName(typed);
  const first = p.first ?? typed.trim();
  const created = unwrap(
    await supabase
      .from('contacts')
      .insert({ first_name: first, last_name: p.last, suffix: p.suffix, display_name: typed.trim(), source: 'manual' })
      .select('id')
      .single(),
  );
  return { id: created.id, name: [first, p.last].filter(Boolean).join(' ') };
}

/** New deal. Only type, name and person are needed; everything else can come later. */
export function NewDealForm({ contact, orgId, buildingId, defaultName }: { contact?: { id: string; name: string }; orgId?: string | null; buildingId?: string | null; defaultName?: string }) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();
  const [dealType, setDealType] = useState<DealType>('tenant_rep');
  const [name, setName] = useState(defaultName ?? '');
  const [person, setPerson] = useState<{ id: string; name: string; isNew?: boolean } | null>(contact ?? null);

  const newContact = useMutation({
    mutationFn: createNameOnlyContact,
    onSuccess: (c) => {
      setPerson({ ...c, isNew: true });
      qc.invalidateQueries({ queryKey: ['contacts'] });
    },
  });
  const save = useMutation({
    mutationFn: () => {
      if (!person) throw new Error('Pick the person this deal is with.');
      if (!name.trim()) throw new Error('Give the deal a name.');
      return createDeal({ dealType, name: name.trim(), primaryContactId: person.id, orgId, buildingId });
    },
    onSuccess: (id) => {
      qc.invalidateQueries({ queryKey: ['pipeline'] });
      if (person?.isNew) {
        toast({
          message: (
            <>
              Don’t forget to finish <b>{person.name}</b>’s contact info — right now it’s just a name.
            </>
          ),
          action: { label: `Open ${person.name}`, to: `/contacts/${person.id}` },
        });
      }
      navigate(`/deals/${id}`);
    },
  });
  return (
    <div className="space-y-3">
      <Field label="Deal type">
        <Select value={dealType} onChange={(e) => setDealType(e.target.value as DealType)}>
          {DEAL_TYPES.map((d) => (
            <option key={d.key} value={d.key}>
              {d.label}
            </option>
          ))}
        </Select>
      </Field>
      <Field label="Deal name">
        <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Patel OB/GYN – Alpharetta relocation" autoFocus />
      </Field>
      <FieldGroup label="Primary contact">
        {person ? (
          <div className="flex items-center justify-between gap-2 rounded-md border border-slate-200 px-3 py-1.5 text-sm">
            <span>
              {person.name}
              {person.isNew && (
                <Pill color="green" className="ml-2">
                  new contact
                </Pill>
              )}
            </span>
            {!contact && (
              <button className="text-xs text-indigo-600 hover:underline" onClick={() => setPerson(null)}>
                change
              </button>
            )}
          </div>
        ) : (
          <ContactPicker onPick={(o) => setPerson({ id: o.id, name: o.label })} onCreate={(typed) => typed && newContact.mutate(typed)} />
        )}
      </FieldGroup>
      <ErrorBox error={newContact.error ?? save.error} />
      <div className="flex justify-end">
        <Button variant="primary" busy={save.isPending || newContact.isPending} onClick={() => save.mutate()}>
          Create deal
        </Button>
      </div>
    </div>
  );
}
